// The app running inside Claude, as the Beiing Human MCP App.
//
// The connector (beiing-human-mcp) serves a small loader page into Claude's
// sandboxed frame. The loader signs in through the connector, sets
// `window.__BH_EMBED__`, and only then loads this bundle from
// app.beiinghuman.com. In a browser `window.__BH_EMBED__` is undefined and
// nothing in this file does anything: `isEmbedded` is false, no storage is
// written, and axios and fetch are left alone.
//
// Inside Claude the frame cannot reach Tier B itself (no cookies, no CORS, and
// Claude's CSP), so every Tier B request goes through the connector's
// app-only `tierb_request` tool via `__BH_EMBED__.request`. The connector
// attaches the user's own token, so Tier B applies the same permissions it
// applies to the web app. Imported first in index.js so the session and the
// transport are in place before any other module runs.
//
// __BH_EMBED__ = {
//   initialPath: '/home' | '/canvas/<id>',
//   theme: 'light' | 'dark',                         // Claude's own
//   session: { token, cognitoUsername },  // token: claims only, unsigned
//   request({ method, url, data, headers, responseType }) →
//     Promise<{ status, headers, data }>   // url is relative to the Tier B base
// }

import axios, { AxiosError, AxiosHeaders } from 'axios';
import { baseUrl, userRoles } from '../constants/appConstants';
import { clearSessionPreservingTab, TAB_ID_KEY } from '../utils/tabSession';
import { roleFromPayload } from '../utils/userRole';
import { THEME_STORAGE_KEY } from '../config/themes';

const embed = typeof window !== 'undefined' ? window.__BH_EMBED__ : undefined;

export const isEmbedded = Boolean(embed);

const claimsOf = (token) => {
  try {
    const part = String(token).split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(part.padEnd(Math.ceil(part.length / 4) * 4, '=')));
  } catch (e) {
    return {};
  }
};

const embedRole = embed ? roleFromPayload(claimsOf(embed.session?.token)) : undefined;

// Admins and vendors have their own home screens; everyone else opens where
// Claude asked (a document, or the inbox).
const roleHome = (path) => {
  if (path !== '/home') return path;
  if (embedRole === userRoles.ADMIN) return '/admin';
  if (embedRole === userRoles.VENDOR) return '/vendor';
  return path;
};
export const embedInitialPath = roleHome(embed?.initialPath || '/home');

// Write the connector's sign-in into the session the way a page refresh finds
// it: token + Cognito username + role. The token carries the user's claims for
// the app to decode but no signature: it is never a credential. The connector
// authenticates each call. embedBootstrap.js then loads the profile before the
// first render.
function seedSession({ token, cognitoUsername }) {
  if (sessionStorage.getItem('cognitoUsername') !== cognitoUsername) {
    clearSessionPreservingTab();
  }
  // Every view in a Claude tab shares one sessionStorage, so each gets its own
  // redux-persist key instead of overwriting the others'.
  sessionStorage.setItem(
    TAB_ID_KEY,
    'tab_claude_' + Math.random().toString(36).slice(2, 10) + '_' + Date.now()
  );
  sessionStorage.setItem('token', token);
  sessionStorage.setItem('cognitoUsername', cognitoUsername);
  if (embedRole) sessionStorage.setItem('userRole', embedRole);
}

const tierbBase = baseUrl();
// Anything that isn't Tier B (a signed S3 URL, say) still goes out directly.
const browserAdapter = axios.getAdapter?.(axios.defaults.adapter);

async function connectorRequest(req) {
  try {
    return await embed.request(req);
  } catch (e) {
    // The connector itself failed (not Tier B): there is no response to hand back.
    throw new AxiosError(e?.message || 'Network Error', AxiosError.ERR_NETWORK);
  }
}

async function connectorAdapter(config) {
  const url = axios.getUri(config);
  if (!url.startsWith(tierbBase)) return browserAdapter(config);

  const res = await connectorRequest({
    method: (config.method || 'get').toUpperCase(),
    url: url.slice(tierbBase.length),
    data: config.data,
    headers: AxiosHeaders.from(config.headers).toJSON(),
    responseType: config.responseType
  });
  const response = {
    data: res.data,
    status: res.status,
    statusText: '',
    headers: AxiosHeaders.from(res.headers || {}),
    config,
    request: {}
  };
  if (!config.validateStatus || config.validateStatus(res.status)) return response;
  throw new AxiosError(
    `Request failed with status code ${res.status}`,
    res.status >= 500 ? AxiosError.ERR_BAD_RESPONSE : AxiosError.ERR_BAD_REQUEST,
    config,
    response.request,
    response
  );
}

// The few calls that use fetch rather than axios (services/common.js request()).
export function wrapFetch(nativeFetch) {
  return async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url;
    if (!url || !url.startsWith(tierbBase)) return nativeFetch(input, init);
    const headers = {};
    new Headers(init.headers || {}).forEach((v, k) => (headers[k] = v));
    const res = await connectorRequest({
      method: (init.method || 'GET').toUpperCase(),
      url: url.slice(tierbBase.length),
      data: init.body,
      headers
    });
    return new Response(res.data ?? null, { status: res.status, headers: res.headers });
  };
}

// Email bodies come from outside the company. In the browser the activity feed
// renders them as HTML, as it always has; inside Claude, where a script would
// sit next to the connector and the conversation, they are shown as text.
export function emailBodyProps(html) {
  if (!isEmbedded) return { dangerouslySetInnerHTML: { __html: html } };
  // DOMParser builds an inert document: nothing in it loads or runs.
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  doc.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
  doc.querySelectorAll('p, div, li, tr').forEach((el) => el.append('\n'));
  return { children: doc.body.textContent.trim(), style: { whiteSpace: 'pre-wrap' } };
}

if (embed) {
  seedSession(embed.session);
  // Follow Claude's light/dark from the first frame, so the app never has to
  // reload itself to change theme (see contexts/themeContext.jsx).
  try {
    localStorage.setItem(THEME_STORAGE_KEY, embed.theme === 'light' ? 'light' : 'dark');
  } catch (e) {
    // storage unavailable: the default theme applies.
  }
  axios.defaults.adapter = connectorAdapter;
  window.fetch = wrapFetch(window.fetch.bind(window));
}
